import React from 'react';
import { Row } from '@/lib/utils';
import { MediumText, RegularText } from '@/lib/Text';
import SvgIcon from '@/components/SvgIcon';
import { colors } from '@/lib/colors';
import {
    ExceptionRateRule,
    ExceptionSettlementMode,
    formatExceptionRule,
    isHiddenInMode,
} from '@/features/coachCommissionTable';
import ExceptionRatesGuide, { useExceptionRatesGuide } from './ExceptionRatesGuide';

/**
 * 요율 탭(상세·세션별 정산은 구간표 아래, 기본 정산은 강사별 칸)의 예외 수업료 자리.
 * - 규칙 0개: 기존 「+ 추가하기」와 같은 문법의 한 줄 링크(「+ 예외 수업료 설정」)만.
 * - 규칙 있음: 요약 한 줄 + 「수정」.
 * 기본 정산에서는 쓰지 않는 「재등록 수강권 전체」 예외는 요약에서 뺀다(저장값은 그대로).
 * 입구를 보일지는 호출부가 isExceptionRatesVisibleInMode 로 정한다.
 */
const ExceptionRatesSummary = ({
    rules,
    readOnly,
    categoryName,
    settlementMode = 'tier',
    onOpen,
}: {
    rules: ExceptionRateRule[];
    settlementMode?: ExceptionSettlementMode;
    readOnly: boolean;
    categoryName: (key: string) => string;
    onOpen: () => void;
}) => {
    const guide = useExceptionRatesGuide();
    // 「예외 수업료란?」 — 무엇인지 모르는 사람이 창을 열기 전에도 볼 수 있게 자리 옆에 둔다.
    const help = (
        <>
            <button type="button" onClick={guide.show} className="shrink-0 text-[13px] leading-[20px] text-[#6B7280] underline underline-offset-4 hover:text-primary">
                예외 수업료란?
            </button>
            {guide.open && <ExceptionRatesGuide onClose={guide.close} settlementMode={settlementMode} />}
        </>
    );
    const shownRules = rules.filter((rule) => !isHiddenInMode(rule, settlementMode));
    if (shownRules.length === 0) {
        if (readOnly) return null;
        return (
            <Row className="mb-[40px] items-center gap-[14px] self-start">
                <Row className="items-center cursor-pointer gap-[8px]" onClick={onOpen}>
                    <SvgIcon name="SvgPlus" size={16} fill={colors.primary} />
                    <RegularText fontSize={16} color={colors.primary}>
                        예외 수업료 설정
                    </RegularText>
                </Row>
                {help}
            </Row>
        );
    }
    const texts = shownRules.map((rule) => formatExceptionRule(rule, categoryName, settlementMode));
    // 한 줄에 다 안 들어가면 말줄임으로 값이 잘린다 — 앞의 두 개만 보이고 나머지는 개수로 알린다.
    const summaryText = texts.length > 2 ? `${texts.slice(0, 2).join(' · ')} 외 ${texts.length - 2}개` : texts.join(' · ');
    return (
        <Row className="items-center justify-between mb-[40px] px-[16px] py-[12px] rounded-[10px] bg-[#F6F8FA] gap-[12px]">
            <Row className="items-center gap-[12px] min-w-0">
                <MediumText fontSize={15} color="#3D4244" className="whitespace-nowrap">
                    예외 수업료
                </MediumText>
                {help}
                <span className="truncate" title={texts.join('\n')}>
                    <RegularText fontSize={14} color="#3D4244">
                        {summaryText}
                    </RegularText>
                </span>
            </Row>
            <div
                className="shrink-0 h-[32px] px-[14px] rounded-[8px] border border-solid border-[#D9D9D9] bg-white flex items-center cursor-pointer hover:bg-[#F2F4F6]"
                onClick={onOpen}
            >
                <RegularText fontSize={14} color={colors.primary}>
                    {readOnly ? '보기' : '수정'}
                </RegularText>
            </div>
        </Row>
    );
};

export default ExceptionRatesSummary;
